<template>
  <div>
    <mia-panel title="Buchungsoptionen">
      <template #default>
        <mia-wait for="fetching booking options">
          <template #waiting>
            <div class="flex justify-center">
              <mia-wave-loader />
            </div>
          </template>

          <div class="flex p-4 space-x-3">
            <div class="flex-grow">
              <input
                v-model="filters.query"
                type="text"
                class="form-input"
                placeholder="Buchungsoptionen durchsuchen"
              >
            </div>
            <div>
              <mia-select v-model="filters.costCenter">
                <option value="">
                  Nach Kostenstelle filtern
                </option>
                <option
                  v-for="costCenter in costCenters"
                  :key="costCenter.id"
                  :value="costCenter.id"
                >
                  {{ costCenter.item_title }}
                </option>
              </mia-select>
            </div>
            <div>
              <mia-submit-button
                label="Buchungsoption anlegen"
                size="small"
                @click="$router.push({ name: 'system-booking-options-create' })"
              />
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th
                  class="sticky w-full p-4 bg-gray-400"
                  style="top: -10px;"
                >
                  Titel
                </th>
                <th
                  class="sticky p-4 bg-gray-400"
                  style="top: -10px;"
                >
                  Kostenstelle
                </th>
                <th
                  class="sticky p-4 bg-gray-400"
                  style="top: -10px;"
                >
                  Verwendet
                </th>
                <th
                  class="sticky p-4 bg-gray-400"
                  style="top: -10px;"
                >
                  Kosten
                </th>
                <th
                  class="sticky p-4 bg-gray-400"
                  style="top: -10px;"
                >
                  Finanzdienstleistungen
                </th>
                <th
                  class="sticky p-4 bg-gray-400"
                  style="top: -10px;"
                >
                  Zubehörverkäufe
                </th>
                <th
                  class="sticky p-4 bg-gray-400"
                  style="top: -10px;"
                >
                  Aktion
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(option, index) in filteredBookingOptions"
                :key="option.id"
              >
                <td
                  :class="{ 'bg-gray-200': index === 0 || index % 2 === 0 }"
                  class="p-4"
                >
                  <input
                    v-if="isOptionEditing(option)"
                    v-model="editingOption.item_title"
                    type="text"
                    class="form-input"
                    :data-id="option.id"
                    @keydown.enter="updateOption(editingOption)"
                  >
                  <span v-else>
                    {{ option.item_title }}
                  </span>
                </td>
                <td
                  :class="{ 'bg-gray-200': index === 0 || index % 2 === 0 }"
                  class="p-4"
                >
                  <mia-select @change="updateCostCenter(option, $event.target.value)">
                    <option value="">
                      Ohne Kostenstelle
                    </option>
                    <option
                      v-for="costCenter in costCenters"
                      :key="costCenter.id"
                      :value="costCenter.id"
                      :selected="costCenter.id === option.item_cost_center_id"
                    >
                      {{ costCenter.item_title }}
                    </option>
                  </mia-select>
                </td>
                <td
                  :class="{ 'bg-gray-200': index === 0 || index % 2 === 0 }"
                  class="p-4 text-center"
                >
                  {{ option.item_usage_count }}
                </td>
                <td
                  :class="{ 'bg-gray-200': index === 0 || index % 2 === 0 }"
                  class="p-4 text-center"
                >
                  <mia-checkbox
                    class="w-5 h-5 cursor-pointer"
                    :checked="option.item_costs === 'Y'"
                    :disabled="option.item_editable === 'N'"
                    @input="updateGroup(option, $event.target.checked, 'costs')"
                  />
                </td>
                <td
                  :class="{ 'bg-gray-200': index === 0 || index % 2 === 0 }"
                  class="p-4 text-center"
                >
                  <mia-checkbox
                    class="w-5 h-5 cursor-pointer"
                    :checked="option.item_financial === 'Y'"
                    :disabled="option.item_editable === 'N'"
                    @input="updateGroup(option, $event.target.checked, 'financial')"
                  />
                </td>
                <td
                  :class="{ 'bg-gray-200': index === 0 || index % 2 === 0 }"
                  class="p-4 text-center"
                >
                  <mia-checkbox
                    class="w-5 h-5 cursor-pointer"
                    :checked="option.item_accessories === 'Y'"
                    :disabled="option.item_editable === 'N'"
                    @input="updateGroup(option, $event.target.checked, 'accessories')"
                  />
                </td>
                <td
                  :class="{ 'bg-gray-200': index === 0 || index % 2 === 0 }"
                  class="p-4 text-center whitespace-no-wrap"
                >
                  <div
                    v-if="option.item_editable === 'Y'"
                    class="flex justify-end space-x-3"
                  >
                    <button
                      v-if="!isOptionEditing(option)"
                      type="button"
                      class="text-orange-mia hover:text-orange-mia-dark hover:underline"
                      title="Buchungsoption bearbeiten"
                      @click="edit(option)"
                    >
                      Bearbeiten
                    </button>
                    <button
                      v-else
                      type="button"
                      class="text-orange-mia hover:text-orange-mia-dark hover:underline"
                      title="Änderungen speichern"
                      @click="updateOption(editingOption)"
                    >
                      Speichern
                    </button>

                    <button
                      v-if="!isOptionEditing(option)"
                      type="button"
                      class="text-orange-mia hover:text-orange-mia-dark hover:underline"
                      title="Buchungsoption Löschen"
                      @click="removeOption(option)"
                    >
                      Löschen
                    </button>
                    <button
                      v-if="isOptionEditing(option)"
                      type="button"
                      class="text-orange-mia hover:text-orange-mia-dark hover:underline"
                      title="Abbrechen"
                      @click="editingOption = null"
                    >
                      Abbrechen
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </mia-wait>
      </template>

      <template #controls>
        <mia-submit-button
          label="Buchungsoption anlegen"
          @click="$router.push({ name: 'system-booking-options-create' })"
        />
      </template>
    </mia-panel>

    <mia-modal
      v-if="deletingOption !== null"
      id="delete-bookingoption-confirmation-modal"
      classes="v--modal flex flex-col"
      width="60%"
      height="auto"
      :show="deletingOption !== null"
      @closed="deletingOption = null"
    >
      <mia-modal-header>
        <h3 class="py-0 my-0">
          Buchungsoption löschen
        </h3>
      </mia-modal-header>

      <div class="p-4">
        <p>
          Die Buchungsoption <b>{{ deletingOption.item_title }}</b> ist mehreren Posten zugeordnet.<br>Bitte wählen Sie die Buchungsoption aus, zu der die Zuordnungen migriert werden sollen.
        </p>

        <p
          v-if="migratableBookingOptions.length === 0"
          class="font-bold"
        >
          Bitte legen Sie zuerst eine weitere Buchungsoption an, damit die Daten migriert werden können!
        </p>
        <mia-select
          v-else
          v-model="migrationBookingOptionId"
          class="w-full"
        >
          <option
            value=""
            disabled
          >
            Neue Buchungsoption auswählen
          </option>
          <option
            v-for="option in migratableBookingOptions"
            :key="option.id"
            :value="option.id"
          >
            {{ option.item_title }}
          </option>
        </mia-select>
      </div>

      <mia-modal-footer class="flex items-center justify-end space-x-3">
        <mia-cancel-button
          label="Abbrechen"
          type="button"
          @click="deletingOption = null; migrationBookingOptionId = '';"
        />

        <mia-submit-button
          label="Buchungsoption löschen"
          type="button"
          :disabled="migrationBookingOptionId === ''"
          :is-loading="$wait.is('deleting cost center')"
          :class="{ 'opacity-50': migrationBookingOptionId === '' }"
          @click="deleteOption(deletingOption, migrationBookingOptionId)"
        />
      </mia-modal-footer>
    </mia-modal>
  </div>
</template>

<script>
import sortBy from 'lodash/sortBy'
import {
  deleteBookingOption, getBookingOptions, getCostCenters, updateBookingOption
} from '@/api/bookingOptions'
import CancelButton from '@/components/cars/edit/CancelButton.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import Checkbox from '@/components/ui/inputs/Checkbox.vue'
import Panel from '@/components/ui/widgets/Panel.vue'
import { tryFocus, trySelect } from '@/helpers/utils'

export default {
  components: {
    'mia-cancel-button': CancelButton,
    'mia-checkbox': Checkbox,
    'mia-panel': Panel,
    'mia-submit-button': SubmitButton
  },

  data: () => ({
    bookingOptions: [],
    costCenters: [],
    editingOption: null,
    deletingOption: null,
    migrationBookingOptionId: '',
    filters: {
      query: '',
      costCenter: ''
    }
  }),

  computed: {
    sortedBookingOptions() {
      return sortBy(this.bookingOptions, ['item_title'])
    },

    filteredBookingOptions() {
      let bookingOptions = this.sortedBookingOptions

      if (this.filters.query.length > 0) {
        bookingOptions = bookingOptions.filter(bookingOption => bookingOption.item_title.toLowerCase().includes(this.filters.query.toLowerCase()))
      }

      if (this.filters.costCenter !== '') {
        bookingOptions = bookingOptions.filter(bookingOption => bookingOption.item_cost_center_id === this.filters.costCenter)
      }

      return bookingOptions
    },

    migratableBookingOptions() {
      return this.sortedBookingOptions.filter(option => option.id !== this.deletingOption.id)
    }
  },

  created() {
    window.eventBus.$emit('mia-set-breadcrumb', [
      {
        link: {
          path: '/system'
        },
        text: 'System'
      },
      {
        link: {
          path: '/system/booking-options/manage'
        },
        text: 'Buchungsoptionen'
      }
    ])

    this.fetchBookingOptions()
    this.fetchBookingOptionCostCenters()
  },

  methods: {
    async fetchBookingOptions() {
      this.$wait.start('fetching booking options')
      const [error, bookingOptions] = await getBookingOptions()
      this.$wait.end('fetching booking options')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Buchungsoptionen konnten nicht geladen werden.']
        })

        return
      }

      this.bookingOptions = bookingOptions
    },

    async fetchBookingOptionCostCenters() {
      this.$wait.start('fetching booking option cost centers')
      const [error, costCenters] = await getCostCenters()
      this.$wait.end('fetching booking option cost centers')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Gruppen konnten nicht geladen werden.']
        })

        return
      }

      this.costCenters = costCenters
    },

    async updateGroup(option, isChecked, field) {
      const index = this.bookingOptions.findIndex(o => o.id === option.id)

      this.$set(this.bookingOptions, index, {
        ...option,
        [`item_${field}`]: isChecked ? 'Y' : 'N'
      })

      this.updateOption(this.bookingOptions[index])
    },

    async updateOption(option) {
      const [error, updatedOption] = await updateBookingOption(option.id, option)

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Buchungsoption konnte nicht aktualisiert werden.']
        })
        return
      }

      this.bookingOptions = this.bookingOptions.map(o => (o.id === option.id ? updatedOption : o))
      this.editingOption = null

      window.eventBus.$emit('mia-info', {
        info: ['Buchungsoption erfolgreich aktualisiert.']
      })
    },

    edit(option) {
      this.editingOption = { ...option }

      this.$nextTick(() => {
        const input = this.$el.querySelector(`input[data-id="${option.id}"]`)

        tryFocus(input)
        trySelect(input)
      })
    },

    isOptionEditing(option) {
      return this.editingOption?.id === option.id
    },

    updateCostCenter(option, newCostCenterId) {
      this.updateOption({ ...option, item_cost_center_id: newCostCenterId })
    },

    removeOption(option) {
      if (option.item_usage_count === 0) {
        this.deleteOption(option)
        return
      }

      this.deletingOption = option
    },

    async deleteOption(option, migrateOptionId = null) {
      const [error] = await deleteBookingOption(option.id, migrateOptionId)

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Buchungsoption konnte nicht gelöscht werden.']
        })
        return
      }

      this.fetchBookingOptions()
      this.deletingOption = null
      this.migrationBookingOptionId = ''

      window.eventBus.$emit('mia-info', {
        info: ['Buchungsoption erfolgreich gelöscht.']
      })
    }
  }
}
</script>
